/**
 * RecordRefundDrawer.jsx
 *
 * Canonical Record-Refund drawer used across the app.
 *
 * Backend: POST /api/payments/v2/files/{pf_id}/record-refund
 * Backend: POST /api/payments/v2/files/{pf_id}/clear-refund
 *
 * The same backend mirrors the refund into pm_payment_file_utilities,
 * pm_payment_file_bills, AND the global `bills` collection (with
 * _user_edited_fields protection), so refund data is identical across:
 *   - Org > Bills (BillsPage)
 *   - Org > Payments > Payment Files (PaymentFileDetail)
 *   - Org > Payments > PF Bills (PaymentsBillsWorkspace)
 *   - SuperAdmin > Payments > Global Workspace (PaymentsBillsWorkspace, isGlobal)
 *
 * Props:
 *   open                bool
 *   onOpenChange        (boolean) => void
 *   bill                { bill_id, payment_file_id, account_id, account_number?, supplier_name?,
 *                         vendor_name?, paid_amount?, total_amount_due?, refund?, refund_amount?,
 *                         refunded_by?, refund_method?, refund_date?, refund_completed?,
 *                         refund_reference?, refund_to? }
 *   userId, userName    current user (for created_by + recorded_by display)
 *   orgUsers            array of {id,name,role} for "Refunded By" select (optional)
 *   orgName             customer name (default refund_to) (optional)
 *   onSaved             () => void   — called after successful save
 *   onCleared           () => void   — called after a clear/undo (edit mode only)
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { ArrowRightLeft, Loader2, Trash2 } from 'lucide-react';
import { PaidBySelect, PAID_BY_OPTIONS } from './PaidBySelect';

const fmtC = (v) =>
  v == null ? '—' : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const todayISO = () => new Date().toISOString().split('T')[0];

export default function RecordRefundDrawer({
  open,
  onOpenChange,
  bill,
  userId,
  userName,
  orgUsers = [],
  orgName = '',
  partnerName,
  onSaved,
  onCleared,
}) {
  const isEdit = !!(bill && (bill.refund === 'yes' || bill.refund_amount));
  const [form, setForm] = useState({
    refund_amount: '',
    refunded_by: '',
    refunded_by_id: '',
    refund_method: '',
    refund_date: todayISO(),
    refund_reference: '',
    refund_to: '',
    refund_completed: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !bill) return;
    // Default "Refund To" recipient: prefer existing value, else leave empty so the
    // user must consciously pick one of the canonical 3 buckets before saving.
    const initialRefundTo = bill && bill.refund_to && PAID_BY_OPTIONS.includes(bill.refund_to)
      ? bill.refund_to
      : '';
    // Default "Refunded By" id mapping. Map stored entity strings to the
    // dropdown's known keys so the saved value is reflected in the form on
    // edit (instead of defaulting to the logged-in user).
    const _storedBy = bill.refunded_by || '';
    let initialRefundedById = '';
    if (_storedBy === 'BillPortal')      initialRefundedById = 'billportal';
    else if (_storedBy === 'Anchor' || (partnerName && _storedBy === partnerName)) initialRefundedById = 'anchor';
    else if (_storedBy === '3rd Party Payment') initialRefundedById = '3rd_party';
    else if (orgUsers.find((u) => u.name === _storedBy)) {
      initialRefundedById = orgUsers.find((u) => u.name === _storedBy).id;
    } else {
      // Fallback for legacy/free-text values — default to BillPortal so the
      // operator sees the canonical entity and can override if needed.
      initialRefundedById = 'billportal';
    }
    if (isEdit) {
      setForm({
        refund_amount: bill.refund_amount || '',
        refunded_by: _storedBy && PAID_BY_OPTIONS.includes(_storedBy) ? _storedBy : 'BillPortal',
        refunded_by_id: initialRefundedById,
        refund_method: bill.refund_method || '',
        refund_date: (bill.refund_date || todayISO()).split('T')[0],
        refund_reference: bill.refund_reference || '',
        refund_to: initialRefundTo,
        refund_completed: !!bill.refund_completed,
      });
    } else {
      setForm({
        refund_amount: '',
        refunded_by: 'BillPortal',
        refunded_by_id: 'billportal',
        refund_method: '',
        refund_date: todayISO(),
        refund_reference: '',
        refund_to: '',
        refund_completed: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bill?.bill_id]);

  const findUtilityId = useCallback(async () => {
    if (!bill?.payment_file_id) return null;
    // PaymentsBillsWorkspace path: bill already has utility info via workspace endpoint
    if (bill.payment_file_utility_id) return bill.payment_file_utility_id;
    if (bill.utility_id) return bill.utility_id;
    // Fallback for callers like Org > Bills: look up via the file's utilities endpoint
    try {
      const res = await api.get(`/payments/v2/files/${bill.payment_file_id}/utilities`, {
        params: { account_id: bill.account_id, account_number: bill.account_number, limit: 50 },
      });
      const m = (res.data?.utilities || []).find((u) => u.bill_id === bill.bill_id);
      return m?.payment_file_utility_id || null;
    } catch {
      return null;
    }
  }, [bill]);

  const save = async () => {
    if (!form.refund_amount || parseFloat(form.refund_amount) <= 0) {
      toast.error('Valid refund amount required');
      return;
    }
    if (!form.refund_to || !PAID_BY_OPTIONS.includes(form.refund_to)) {
      toast.error('Please select who the refund is going to (BillPortal, Anchor, or Customer)');
      return;
    }
    if (!bill?.payment_file_id) {
      toast.error('This bill is not in a Payment File. Refund is only available for PF bills.');
      return;
    }
    setSaving(true);
    try {
      const utility_id = await findUtilityId();
      if (!utility_id) {
        toast.error('Could not find utility record for this bill');
        setSaving(false);
        return;
      }
      await api.post(`/payments/v2/files/${bill.payment_file_id}/record-refund`, {
        account_id: bill.account_id,
        utility_id,
        refund_amount: parseFloat(form.refund_amount),
        refunded_by: form.refunded_by || userName || 'BillPortal',
        refund_method: form.refund_method || '',
        refund_date: form.refund_date || todayISO(),
        refund_reference: form.refund_reference || '',
        refund_to: form.refund_to,
        refund_completed: !!form.refund_completed,
        user_id: userId,
        user_name: userName,
        created_by: userId,
        created_by_name: userName,
      });
      toast.success(isEdit ? 'Refund updated' : 'Refund recorded');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record refund');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!bill?.payment_file_id) return;
    setSaving(true);
    try {
      const utility_id = await findUtilityId();
      if (!utility_id) {
        toast.error('Could not find utility record');
        setSaving(false);
        return;
      }
      await api.post(`/payments/v2/files/${bill.payment_file_id}/clear-refund`, {
        account_id: bill.account_id,
        utility_id,
        refund_amount: 0,
        user_id: userId,
        user_name: userName,
      });
      toast.success('Refund cleared');
      onCleared?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear refund');
    } finally {
      setSaving(false);
    }
  };

  if (!bill) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="sm:max-w-[420px] w-[420px] flex flex-col p-0"
        data-testid="record-refund-drawer"
      >
        <SheetHeader className="px-6 py-4 border-b border-border">
          <SheetTitle className="flex items-center gap-2 text-base">
            <ArrowRightLeft className="h-4 w-4 text-red-600" />
            {isEdit ? 'Edit' : 'Record'} Refund
          </SheetTitle>
          <div className="text-xs text-muted-foreground space-y-0.5">
            <p>
              {bill.supplier_name || bill.vendor_name || ''} &bull; {bill.account_number || ''}
            </p>
            <p>
              Paid Amount:{' '}
              <strong className="text-emerald-700">{fmtC(bill.paid_amount)}</strong>
            </p>
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">
              Refund Amount *
            </label>
            <Input
              type="number"
              className="h-9 text-sm font-mono"
              value={form.refund_amount || ''}
              onChange={(e) => setForm((p) => ({ ...p, refund_amount: e.target.value }))}
              step="0.01"
              placeholder="0.00"
              data-testid="refund-amount-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">
              Refund To *
            </label>
            <PaidBySelect
              value={form.refund_to}
              onChange={(v) => setForm((p) => ({ ...p, refund_to: v }))}
              partnerName={partnerName}
              customerLabel={orgName || undefined}
              testId="refund-to-select"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Where the refunded money is going (canonical 3-bucket)
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">
              Refunded By
            </label>
            {orgUsers.length > 0 ? (
              <Select
                value={form.refunded_by_id || userId || '3rd_party'}
                onValueChange={(v) => {
                  if (v === '3rd_party') {
                    setForm((p) => ({ ...p, refunded_by_id: '', refunded_by: '3rd Party Payment' }));
                  } else if (v === 'billportal') {
                    setForm((p) => ({ ...p, refunded_by_id: 'billportal', refunded_by: 'BillPortal' }));
                  } else if (v === 'anchor') {
                    setForm((p) => ({ ...p, refunded_by_id: 'anchor', refunded_by: partnerName || 'Anchor' }));
                  } else {
                    const u = orgUsers.find((ou) => ou.id === v);
                    setForm((p) => ({ ...p, refunded_by_id: v, refunded_by: u?.name || p.refunded_by }));
                  }
                }}
              >
                <SelectTrigger className="h-9 text-sm" data-testid="refunded-by-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="billportal">BillPortal</SelectItem>
                  <SelectItem value="anchor">{partnerName || 'Anchor'}</SelectItem>
                  <SelectItem value="3rd_party">3rd Party Payment</SelectItem>
                  {orgUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} {u.role ? `(${u.role})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                className="h-9 text-sm"
                value={form.refunded_by || ''}
                onChange={(e) => setForm((p) => ({ ...p, refunded_by: e.target.value }))}
                placeholder="BillPortal"
                data-testid="refunded-by-input"
              />
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">
              Refund Date
            </label>
            <Input
              type="date"
              className="h-9 text-sm"
              value={form.refund_date ? form.refund_date.split('T')[0] : ''}
              onChange={(e) => setForm((p) => ({ ...p, refund_date: e.target.value }))}
              data-testid="refund-date-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">
              Refund Method / Notes
            </label>
            <textarea
              className="w-full min-h-[60px] p-2 text-sm border border-input bg-background rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-ring"
              value={form.refund_method || ''}
              onChange={(e) => setForm((p) => ({ ...p, refund_method: e.target.value }))}
              placeholder="Credit note, ACH reversal..."
              data-testid="refund-method-input"
            />
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!form.refund_completed}
              onChange={(e) => setForm((p) => ({ ...p, refund_completed: e.target.checked }))}
              data-testid="refund-completed-checkbox"
            />
            <span>Mark as completed (refund already processed)</span>
          </label>
          <div className="bg-red-50/50 border border-red-200 p-3 space-y-1.5 text-xs rounded-md">
            <p className="font-medium text-red-700 uppercase text-[10px] tracking-wider">
              Record Details
            </p>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Refunded By</span>
              <span className="font-medium" data-testid="refund-by-summary">
                {form.refunded_by || 'BillPortal'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Refund To</span>
              <span className="font-medium" data-testid="refund-to-summary">
                {form.refund_to || (bill?.refund_to) || '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Recorded By</span>
              <span className="font-medium" data-testid="recorded-by-summary">
                {userName || 'System'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Date</span>
              <span className="font-medium">
                {form.refund_date ? new Date(form.refund_date).toLocaleDateString() : new Date().toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
        <div className="border-t border-border px-6 py-4 flex items-center gap-2 justify-between">
          <div>
            {isEdit && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs text-red-600 border-red-200"
                onClick={clear}
                disabled={saving}
                data-testid="refund-clear-btn"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="text-xs bg-red-600 hover:bg-red-700 text-white"
              onClick={save}
              disabled={saving}
              data-testid="refund-save-btn"
            >
              {saving ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <ArrowRightLeft className="h-3 w-3 mr-1" />
              )}
              {isEdit ? 'Update' : 'Record'} Refund
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
